<script lang="ts" setup>
import { Settings } from 'luxon';
import AuthService from '../../../../runtime/services/AuthService';

const { $auth } = useAuth();
const { $i18n } = useLocalization();

const menu = ref(null);
const visible = ref(false);

const facePictureUrl = computed(() =>
{
    const { url } = $auth.user()?.facePicture ?? {};

    return url || null;
});

const localesOptions: ComputedRef<Array<{ value: string; text: string }>> = computed(() =>
{
    return Object.entries($i18n.languages()).map(([key, value]) => ({ value: key, text: $t(value) }));
});

const useLangSwitch = computed(() => localesOptions.value.length === 2);

const langSwitchText = computed(() =>
{
    if (localesOptions.value.length < 2) return '';

    return `${getShortLocale(localesOptions.value[0].value)} → ${getShortLocale(localesOptions.value[1].value)}`;
});

const onClick = (): void =>
{
    menu.value.hide();
};

function getShortLocale(locale: string): string
{
    return locale.split('-')[0].toUpperCase();
}

onMounted(() =>
{
    window.addEventListener('click', onClick);
});

onBeforeUnmount(() =>
{
    window.removeEventListener('click', onClick);
});

const profile = (): void =>
{
    onClick();
    navigateTo({ name: 'core-account-profile' });
};

const password = (): void =>
{
    onClick();
    navigateTo({ name: 'core-account-password' });
};

const notifications = (): void =>
{
    onClick();
    navigateTo({ name: 'core-account-notifications' });
};

const logout = (): void =>
{
    onClick();
    $auth.logout();
};

const onToggle = (): void =>
{
    visible.value = !visible.value;
};

const locale = computed({
    get(): string
    {
        return useThemeStore().locale;
    },
    set(value: string)
    {
        useThemeStore().locale = value;
    }
});

const onLangChange = async (value?: string): Promise<void> =>
{
    let newLocale = value ? value : locale.value;

    if (useLangSwitch.value) newLocale = locale.value === localesOptions.value[0].value ? localesOptions.value[1].value : localesOptions.value[0].value;

    if ($auth.check())
    {
        await AuthService.updatePreferredLanguage(newLocale);
    }

    $i18n.setLocale(newLocale);
    locale.value = newLocale;
    Settings.defaultLocale = locale.value;
};

const onLangToggle = (state: boolean): void =>
{
    onLangChange();
};

onBeforeMount(async (): Promise<void> =>
{
    const { preferredLanguage } = $auth.user() ?? {};

    if (!locale.value)
        locale.value = preferredLanguage ?? $i18n.locale();

    if (locale.value && preferredLanguage && locale.value !== preferredLanguage)
        await onLangChange();
    else
    {
        $i18n.setLocale(locale.value);
        Settings.defaultLocale = locale.value;
    }
});
</script>

<template>
    <div id="account" class="d-flex align-self-center">
        <IdeoDropdown
            ref="menu"
            variant="link"
            :toggle-class="{ 'navbar-toggler': true, 'active': visible }"
            right
            no-caret
            @toggle="onToggle"
        >
            <template #button-content>
                <IdeoImg
                    :src="$filters.image(facePictureUrl, 'ico')"
                    rounded="circle"
                    :blank="!facePictureUrl"
                    blank-type="avatar"
                    width="40"
                    height="40"
                    blank-color="#777"
                    :alt="$t('[[[Zdjęcie użytkownika]]]')"
                />
            </template>

            <LayoutsDefaultHeaderAccountItems />

            <IdeoDropdownItem :to="{ name: 'core-account-profile' }" @click.prevent="profile()">
                <i class="icon far fa-user" /> <span>{{ $t('[[[Profil użytkownika]]]') }}</span>
            </IdeoDropdownItem>
            <IdeoDropdownItem :to="{ name: 'core-account-password' }" @click.prevent="password()">
                <i class="icon far fa-lock" /> <span>{{ $t('[[[Zmiana hasła]]]') }}</span>
            </IdeoDropdownItem>
            <IdeoDropdownItem :to="{ name: 'core-account-notifications' }" @click.prevent="notifications()">
                <i class="icon far fa-bell" /> <span>{{ $t('[[[Powiadomienia]]]') }}</span>
            </IdeoDropdownItem>
            <IdeoDropdownItemButton v-if="localesOptions.length > 1" :class="['language', { 'multiple': !useLangSwitch }]" prevent-close @click.stop>
                <i class="icon far fa-language" />
                <span>
                    {{ $t('[[[Wybór języka]]]') }}:
                    <template v-if="useLangSwitch">{{ langSwitchText }}</template>
                </span>
                <IdeoFormCheckbox v-if="useLangSwitch" class="mb-0 ms-auto" :switch="true" :checked="locale === localesOptions[1].value" @change="onLangToggle" />
                <IdeoFormSelect v-else v-model="locale" :options="localesOptions" @update:model-value="onLangChange" />
            </IdeoDropdownItemButton>
            <div class="dropdown-divider" />
            <IdeoDropdownItem :to="{ name: 'dashboard' }" @click.prevent="logout()">
                <i class="icon far fa-right-to-bracket" /> <span>{{ $t('[[[Wyloguj się]]]') }}</span>
            </IdeoDropdownItem>
        </IdeoDropdown>
    </div>
</template>

<style lang="scss">
#account {
    .navbar-toggler {
        img {
            padding: 1px;
            border: 1px solid transparent;
        }

        &:hover img {
            border-color: var(--bs-primary);
        }
    }

    .dropdown {
        &-menu {
            z-index: var(--ideo-dropdown-zindex);
            min-width: 240px;
        }
        &-item {
            &.language {
                cursor: default;
                padding-right: 0.625rem;

                &:active, &:focus, &:hover {
                    background: inherit;
                    color: inherit;
                }

                &.multiple {
                    flex-wrap: wrap;
                }

                select, .select-container {
                    flex-basis: 100%;
                    width: 100%;
                }

                input[type="checkbox"] {
                    cursor: pointer;
                    width: 2rem;
                    height: 1rem;
                }
            }
        }
    }
}
</style>
